// data/aboutUsContent.ts
// Variante con texto e imagen
type ContentSection = {
  heading: string;
  content: string;
  image: string;
  imageAlt?: string;
  imageVariant?: "tall" | "default";
};

// Variante con lista de items
type ItemsSection = {
  heading: string;
  title: string;
  description: string;
  items: { title: string; text: string }[];
};

// Union de ambas
export type Section = ContentSection | ItemsSection;

export const aboutUsContent: {
  title: string;
  intro: string;
  image: string;
  sections: Section[];
} = {
  title: "Who We Are",
  intro: "We Build Awesome People by Building Awesome Systems",
  image: "/assets/team/image88.jpg",

  sections: [
    {
      heading: "We Don’t Predict the Future. We Build It.",
      content: `Seven years ago, New Zealand–born civil engineer Daniel Hungerford-Morgan chose to change the way construction systems work. He crossed the world to Toronto, Canada, left his job and started Hungerford with a simple conviction: build better systems and you will build better people.

What followed wasn’t a launch so much as a discipline, years of listening, designing and building systems, ordering chaos, and iterating again and again. With the steady backing of his family and the first employees who believed in the mission, the practice took shape: clear roles, clean workflows, honest reviews, small gains kept weekly. People rose because the system lifted them; the system improved because people did and is morphing into an industry leading platform.`,
      image: "/assets/team/people-Dan.jpg",
      imageAlt: "Dan at hungerford company",
    },
    {
      heading: "We Move Slow to Move Fast",
      content: `At Hungerford, we change how contractors win and deliver work. We begin deliberately slow to smooth the process, then add controls and strong overlays to catch errors at every point. Our elite estimators and project leads embed seamlessly, producing clean scopes, fast and defensible bids, and handoffs that hold under pressure. 
      
      Clarity over noise, cadence over scramble, accountability over drama. Our operators measure, review, and improve until momentum becomes habit. Performance-driven, system-first, the work shapes the people, the people sharpen the system, and the results compound month after month.`,
      image: "/assets/team/image4.jpg",
    },
    {
      heading: `We Build Beyond the Estimator to shape the Industry`,
      content: `We’re on our way from one estimator to a market-scale platform for the industry’s best. Systems are our key: we train and deploy elite operators with clear scope language, tight quantities, RFQs, pricing logic, and QA checks—delivering fast, accurate, defensible bids with clean handoffs and no extra management. 
      
      Every engagement runs the same disciplined rhythm, scope, build, review, handoff. You get reliable throughput and audit-ready numbers, with less scramble, fewer surprises, and a team that improves every month.`,
      image: "/assets/team/image3.jpg",
    },
    {
      heading: "What We Consider",
      title: "We Turn Clarity into Action",
      description: `Our goal is to build and offer the best estimators in the industry. We cut noise, set simple defaults, and keep only the steps that move our estimators forward. The result is fewer surprises, faster decisions, and time back for the work that counts. The depth stays beneath the surface: systems that read simple because they were made with care.`,
      items: [
        {
          title: "Detail",
          text: "Precise scopes, clean assumptions, consistent method",
        },
        {
          title: "Cadence",
          text: "Daily/weekly rhythms that make speed sustainable.",
        },
        {
          title: "Clarity",
          text: "Tools arranged to reduce friction at action.",
        },
        {
          title: "Review",
          text: "Checkpoints that protect quality without drag.",
        },
      ],
    },
    {
      heading: "We Are Many Minds, One Playbook",
      content: `Great systems are built by different minds seeing the same problem from many angles. At Hungerford, we welcome varied backgrounds, trades, and stories because they sharpen judgment, reveal blind spots, and make the work better for everyone.
      
      Our culture is simple: speak the truth, follow the process, and respect the person across the table. We invite debate, pair strong opinions with stronger listening, and measure ourselves by how well we learn together.`,
      image: "/assets/team/image2.jpg",
    },
  ],
};

export const values = {
  heading: "How Our Values Show Up in Practice",
  items: [
    {
      title: "Follow the Process",
      text: "Shared rhythms make space for every voice to be heard with judgment, not noise.",
      icon: "PrecisionManufacturingIcon",
      iconColor: "#F9A825",
      highlightColor: "#FFCA28",
      iconBg: "#3E2F00",
    },
    {
      title: "Speak the Truth",
      text: "We invite candid feedback; disagreement is treated as care for the work.",
      icon: "EmojiEventsIcon",
      iconColor: "#F9A825",
      highlightColor: "#FFCA28",
      iconBg: "#3E2F00",
    },
    {
      title: "Iterate to Succeed",
      text: "Different perspectives fuel the next better version.",
      icon: "TrendingUpIcon",
      iconColor: "#F9A825",
      highlightColor: "#FFCA28",
      iconBg: "#3E2F00",
    },
    {
      title: "Be Balanced",
      text: "We hold tension, speed and care, cost and craft, by weighing many views.",
      icon: "MoreTimeIcon",
      iconColor: "#F9A825",
      highlightColor: "#FFCA28",
      iconBg: "#3E2F00",
    },
    {
      title: "Take Responsibility",
      text: "Own your impact: on the work, the team, and the client.",
      icon: "PeopleIcon",
      iconColor: "#F9A825",
      highlightColor: "#FFCA28",
      iconBg: "#3E2F00",
    },
    {
      title: "Play the Long Game",
      text: "We grow people over years, not news cycles.",
      icon: "EqualizerIcon",
      iconColor: "#F9A825",
      highlightColor: "#FFCA28",
      iconBg: "#3E2F00",
    },
    {
      title: "See the Big Picture",
      text: "Scope, field, finance, and community are all part of one story.",
      icon: "EngineeringIcon",
      iconColor: "#F9A825",
      highlightColor: "#FFCA28",
      iconBg: "#3E2F00",
    },
    {
      title: "Virtue Over Approval",
      text: "Do what’s right, even when it’s unpopular.",
      icon: "CheckCircleIcon",
      iconColor: "#F9A825",
      highlightColor: "#FFCA28",
      iconBg: "#3E2F00",
    },
    {
      title: "Win–Win–Win",
      text: "Outcomes where the client, the business, and the operator all advance.",
      icon: "EmojiEventsIcon",
      iconColor: "#F9A825",
      highlightColor: "#FFCA28",
      iconBg: "#3E2F00",
    },
  ],
};
